import { track } from '@/utils/analytics';
import { formatHexCode } from '@/UI/ColorPicker/colorPicker.utils';
import { FlowGraph } from '../FlowGraph';
import type { Color, NodeId } from 'web';
import type { ColorPaletteData } from '@/types/node';
import type { WebColor } from '@/UI/ColorPicker/ColorPicker';

const MIN_COLORS = 0;
const MAX_COLORS = 10;

const WHITE: Color = { r: 255, g: 255, b: 255, a: 1.0 };

export class ColorPaletteView {
  // `Color` is a value type that loses identity across WASM round-trips, so we
  // track row identity here in parallel with `colors`.
  private rowKeys: string[] = [];
  private nextKeyId = 0;

  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getColors(): Color[] {
    return this.graph.store.getNodeData<ColorPaletteData>(this.nodeId).colors ?? [];
  }

  /** Stable per-row keys, 1:1 with `getColors()`, so per-row state follows reorders. */
  getRowKeys(): string[] {
    const n = this.getColors().length;
    while (this.rowKeys.length < n) this.rowKeys.push(this.freshKey());
    if (this.rowKeys.length > n) this.rowKeys.length = n;
    return this.rowKeys;
  }

  canRemove(): boolean {
    return this.getColors().length > MIN_COLORS;
  }

  canAdd(): boolean {
    return this.getColors().length < MAX_COLORS;
  }

  setColorAt(index: number, color: WebColor, ongoing: boolean) {
    const colors = this.getColors().slice();
    if (index < 0 || index >= colors.length) return;
    colors[index] = color;
    this.commit(colors, ongoing);
    // Capture the picked color only at commit time (skip the high-frequency
    // ongoing drag events from the picker so we don't flood analytics).
    if (!ongoing) {
      track('color_palette_color_picked', { color: formatHexCode(color) });
    }
  }

  addColor(seed: Color = WHITE) {
    const colors = this.getColors();
    if (colors.length >= MAX_COLORS) return;
    this.rowKeys.push(this.freshKey());
    this.commit([...colors, seed], false);
    track('color_palette_color_added', { color: formatHexCode(seed) });
  }

  insertColorAt(index: number, seed: Color = WHITE) {
    const colors = this.getColors();
    if (colors.length >= MAX_COLORS) return;
    const at = Math.max(0, Math.min(index, colors.length));
    this.rowKeys.splice(at, 0, this.freshKey());
    const next = [...colors.slice(0, at), seed, ...colors.slice(at)];
    this.commit(next, false);
    track('color_palette_color_added', { color: formatHexCode(seed) });
  }

  removeColorAt(index: number) {
    const colors = this.getColors();
    if (colors.length <= MIN_COLORS) return;
    if (index < 0 || index >= colors.length) return;
    this.rowKeys.splice(index, 1);
    const next = colors.slice();
    next.splice(index, 1);
    this.commit(next, false);
  }

  reorderColor(fromIndex: number, toIndex: number) {
    const colors = this.getColors();
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= colors.length ||
      toIndex >= colors.length
    ) {
      return;
    }
    const [movedKey] = this.rowKeys.splice(fromIndex, 1);
    this.rowKeys.splice(toIndex, 0, movedKey);
    const next = colors.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.commit(next, false);
  }

  private commit(colors: Color[], ongoing: boolean) {
    this.graph.wasmEdit((wasm) => {
      return wasm.setColorPaletteColors(this.nodeId, colors);
    }, ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
  }

  private freshKey(): string {
    this.nextKeyId += 1;
    return `color-palette-row-${this.nextKeyId}`;
  }
}
